import { useQueryClient } from "@tanstack/react-query";
import { learningTreeReviewVariables, useLearningTreeReviewEnabled } from "@utils/learningTreeReview";
import { useCallback, useMemo } from "react";

import { useLearnerProgrammingRefresh } from "@hooks/useLearnerProgrammingRefresh";
import { useReport } from "@providers/ReportProvider";

/** Shared hook for refresh-programming (EHR sync). Use in GoalsSyncButton and in sidebar PopoverMenu "Refresh programming". */
export const useManualRefreshProgramming = () => {
  const { report } = useReport();
  const learnerId = report?.learner?.id ?? "";
  const reportId = report?.id ?? "";

  const queryClient = useQueryClient();
  const learningTreeReview = useLearningTreeReviewEnabled();

  const startVariables = useMemo(
    () =>
      reportId
        ? { reportId, reassessmentReportGoalSync: true, ...learningTreeReviewVariables(learningTreeReview) }
        : null,
    [reportId, learningTreeReview],
  );

  const onSyncCompleted = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["get-reassessment-goals", reportId] });
    // Goal/target progress charts (ShortTermGoalProgressGraph, TargetProgressGraph)
    // read from this separate cache key, keyed by goalId/targetId rather than
    // reportId — without this, a graph already mounted before the refresh keeps
    // showing pre-refresh data (PROA-2123).
    queryClient.invalidateQueries({ queryKey: ["GetSessionTargetProgress"] });
    // A sync re-runs the CentralReach learning-tree mapping, which is
    // IN_PROGRESS by now: refetching the report starts the narrative page's
    // "refreshing" hold (and its polling), then shows the fields it filled.
    void queryClient.invalidateQueries({ queryKey: ["get-report-by-id", reportId] });
  }, [queryClient, reportId]);

  return useLearnerProgrammingRefresh({ learnerId, startVariables, onSyncCompleted });
};
